import React, { useEffect, useState } from 'react';
import { getProgramReminders, setProgramReminders } from '../lib/programsApi';
import { Loader2, Sunrise, Moon, Save, BellOff, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ReflectionReminderSettings — Prompt #7 Task 3.
 *
 * Renders opt-in toggles + editable times for Morning Reflection + Evening
 * Reflection reminders, plus a Quiet Hours window. Backed by
 * `GET/POST /api/push/reflection-schedule`. Times are HH:MM in the user's
 * local timezone; the backend scheduler compares them against the user's
 * device timezone captured on push subscription.
 *
 * Nothing is turned on for existing users automatically — every time is
 * `null` (off) until the user picks one and saves.
 *
 * If the user is admin, also shows a "Send test to my device" affordance
 * that only delivers to the admin's own registered push subscriptions.
 */
const ProgramRemindersRow = () => {
  const [state, setState] = useState(null);
  useEffect(() => { getProgramReminders().then(setState).catch(() => setState({ enabled: true, hasPush: false })); }, []);
  const toggle = async () => {
    const next = !state?.enabled;
    setState((s) => ({ ...(s || {}), enabled: next }));
    try { await setProgramReminders(next); } catch { setState((s) => ({ ...(s || {}), enabled: !next })); }
  };
  return (
    <div className="rounded-2xl p-4 bg-[#FFFCF3] border border-stone-200 mb-3" data-testid="reminder-row-program">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Program reminders</p>
          <p className="text-[11px] text-stone-500">Gentle morning and evening nudges for any journey you've started. Uses your reflection times, or 8:00 / 20:30.</p>
        </div>
        <button type="button" role="switch" aria-checked={!!state?.enabled} onClick={toggle} disabled={!state} className={`w-11 h-6 rounded-full relative transition-colors ${state?.enabled ? 'bg-[#D6AD60]' : 'bg-stone-300'}`} data-testid="program-reminders-toggle">
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-[left] ${state?.enabled ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
    </div>
  );
};

const ReflectionReminderSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [morningEnabled, setMorningEnabled] = useState(false);
  const [morningTime, setMorningTime] = useState('07:30');
  const [eveningEnabled, setEveningEnabled] = useState(false);
  const [eveningTime, setEveningTime] = useState('20:00');
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd]   = useState('07:00');
  const [testingWhich, setTestingWhich] = useState(null);

  const isAdmin = !!user?.isAdmin;

  useEffect(() => {
    const token = localStorage.getItem('rbr_token');
    if (!token) { setLoading(false); return; }
    fetch(`${API_URL}/api/push/reflection-schedule`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        if (d.morningTime) { setMorningEnabled(true); setMorningTime(d.morningTime); }
        if (d.eveningTime) { setEveningEnabled(true); setEveningTime(d.eveningTime); }
        if (d.quietHours) {
          setQuietEnabled(!!d.quietHours.enabled);
          if (d.quietHours.start) setQuietStart(d.quietHours.start);
          if (d.quietHours.end)   setQuietEnd(d.quietHours.end);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const r = await fetch(`${API_URL}/api/push/reflection-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          morningTime: morningEnabled ? morningTime : null,
          eveningTime: eveningEnabled ? eveningTime : null,
          quietHours:  { enabled: quietEnabled, start: quietStart, end: quietEnd },
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('Reminder preferences saved');
    } catch (e) {
      toast.error('Could not save reminder preferences');
    } finally {
      setSaving(false);
    }
  };

  const adminTest = async (which) => {
    setTestingWhich(which);
    try {
      const token = localStorage.getItem('rbr_token');
      const r = await fetch(`${API_URL}/api/push/admin-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ which }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Sent ${which} test to ${data.sent}/${data.total} device(s)`);
    } catch (e) {
      toast.error(e.message || 'Test send failed');
    } finally {
      setTestingWhich(null);
    }
  };

  if (loading) return null;

  const Row = ({ icon: Icon, label, sub, enabled, setEnabled, time, setTime, testid, testKey }) => (
    <div className="rounded-2xl p-4 bg-[#FFFCF3] border border-stone-200 mb-3" data-testid={`reminder-row-${testid}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #C79A4A 0%, #6b4c1a 100%)', color: '#0D2B24' }}
          >
            <Icon className="w-5 h-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{label}</p>
            <p className="text-[11px] text-stone-500">{sub}</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled(!enabled)}
          data-testid={`reminder-toggle-${testid}`}
          className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#C79A4A]' : 'bg-stone-300'}`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 bg-[#FFFCF3] rounded-full shadow-sm transition-transform ${enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
          />
        </button>
      </div>
      {enabled && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <label className="text-[12px] text-stone-600">Reminder time</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            data-testid={`reminder-time-${testid}`}
            className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-[13px] text-stone-800"
          />
        </div>
      )}
      {isAdmin && enabled && (
        <button
          type="button"
          onClick={() => adminTest(testKey)}
          disabled={testingWhich === testKey}
          data-testid={`reminder-admin-test-${testid}`}
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#173F35] hover:underline"
        >
          {testingWhich === testKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send test to my device
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="reflection-reminder-settings">
      <p className="rbr-eyebrow mb-2" style={{ color: '#C79A4A' }}>Reflection reminders</p>
      <p className="text-[12px] text-stone-500 mb-3">
        Opt in to gentle morning and evening pings. Nothing turns on automatically.
      </p>

      <Row
        icon={Sunrise}
        label="Morning reflection"
        sub="Start where you are."
        enabled={morningEnabled}
        setEnabled={setMorningEnabled}
        time={morningTime}
        setTime={setMorningTime}
        testid="morning"
        testKey="morning"
      />
      <Row
        icon={Moon}
        label="Evening reflection"
        sub="Let the day soften."
        enabled={eveningEnabled}
        setEnabled={setEveningEnabled}
        time={eveningTime}
        setTime={setEveningTime}
        testid="evening"
        testKey="evening"
      />

      <ProgramRemindersRow />

      <div className="rounded-2xl p-4 bg-[#FFFCF3] border border-stone-200 mb-3" data-testid="reminder-row-quiet-hours">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #4a7c65 0%, #173F35 100%)', color: '#F6F1E7' }}
            >
              <BellOff className="w-5 h-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Quiet hours</p>
              <p className="text-[11px] text-stone-500">No reminders during this window.</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={quietEnabled}
            onClick={() => setQuietEnabled(!quietEnabled)}
            data-testid="reminder-toggle-quiet"
            className={`relative w-11 h-6 rounded-full transition-colors ${quietEnabled ? 'bg-[#C79A4A]' : 'bg-stone-300'}`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 bg-[#FFFCF3] rounded-full shadow-sm transition-transform ${quietEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
            />
          </button>
        </div>
        {quietEnabled && (
          <div className="mt-3 flex items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <label className="text-[12px] text-stone-600">From</label>
              <input
                type="time"
                value={quietStart}
                onChange={(e) => setQuietStart(e.target.value)}
                data-testid="reminder-quiet-start"
                className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-[13px] text-stone-800"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[12px] text-stone-600">Until</label>
              <input
                type="time"
                value={quietEnd}
                onChange={(e) => setQuietEnd(e.target.value)}
                data-testid="reminder-quiet-end"
                className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-[13px] text-stone-800"
              />
            </div>
          </div>
        )}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="reminder-save-btn"
        className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60 active:scale-95 transition-all"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save reminder preferences
      </button>
      <p className="text-[10px] text-stone-400 text-center mt-2">
        Reminders use your device's local time zone. They never include private mood data.
      </p>
    </div>
  );
};

export default ReflectionReminderSettings;
